import {
  Controller,
  type Control,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form';
import { FormControl, InputLabel, MenuItem, Select, type SxProps, type Theme } from '@mui/material';
import React from 'react';

export type Identifiable = {
  id: string | number;
  name: string;
};

type SelectFieldProps<T extends Identifiable, TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  fieldPath: FieldPath<TFieldValues>;
  sx?: SxProps<Theme>;
  items: T[];
  keyExtractor: (item: T) => T['id'];
  labelExtractor: (item: T) => string;
  defaultValue?: FieldPathValue<TFieldValues, FieldPath<TFieldValues>>;
  label: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  multiple?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
};

const SelectField = <T extends Identifiable, TFieldValues extends FieldValues>(
  props: SelectFieldProps<T, TFieldValues>,
) => {
  return (
    <Controller
      name={props.fieldPath}
      control={props.control}
      defaultValue={props.defaultValue}
      render={({ field }) => (
        <FormControl fullWidth sx={props.sx} disabled={props.disabled} size={props.size}>
          <InputLabel id={props.label}>{props.label}</InputLabel>
          <Select
            {...field}
            label={props.label}
            multiple={props.multiple ?? false}
            value={field.value ?? (props.multiple ? [] : '')}
            onChange={e => field.onChange(e.target.value)}
            inputRef={(element: HTMLInputElement) => {
              field.ref(element);

              if (props.inputRef) {
                if (typeof props.inputRef === 'function') {
                  props.inputRef(element);
                } else {
                  props.inputRef.current = element;
                }
              }
            }}
          >
            {props.items.map(item => (
              <MenuItem key={props.keyExtractor(item)} value={props.keyExtractor(item)}>
                {props.labelExtractor(item)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  );
};

export default SelectField;
